import { formatDistanceToNow } from 'date-fns';
import type { BackupStatus } from '../hooks/useStatus';

// A set runs daily; a copy older than this is stale. The restore test runs weekly.
const SET_STALE_MS = 26 * 60 * 60 * 1000;
const RESTORE_STALE_MS = 8 * 24 * 60 * 60 * 1000;
// The AWS restore test runs on the first Sunday of each month: at most five weeks apart.
const AWS_RESTORE_STALE_MS = 36 * 24 * 60 * 60 * 1000;

/** "20261030T195042Z" -> Date, or null. */
export function setIdToDate(id: string | null | undefined): Date | null {
  const m = id ? /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/.exec(id) : null;
  return m ? new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6])) : null;
}

export function ago(d: Date | string | null | undefined): string {
  if (!d) return 'unknown';
  const date = typeof d === 'string' ? new Date(d) : d;
  return Number.isNaN(date.getTime()) ? 'unknown' : `${formatDistanceToNow(date)} ago`;
}

function older(d: Date | string | null | undefined, ms: number, now: number): boolean {
  if (!d) return true;
  const t = (typeof d === 'string' ? new Date(d) : d).getTime();
  return Number.isNaN(t) || now - t > ms;
}

export function formatBytes(n: number | null | undefined): string {
  if (n === null || n === undefined) return 'unknown';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

/** Overall badge for a droplet's backups: healthy only when everything is present, fresh and passing. */
export function backupHealth(b: BackupStatus, now = Date.now()): 'healthy' | 'warning' | 'down' {
  if (b.offsiteStore !== undefined) {
    const s = b.offsiteStore;
    if (!s || s.lastResult === null || s.newestSetId === null) return 'down';
    if (s.lastResult === 'failed') return 'down';
    if (older(setIdToDate(s.newestSetId), SET_STALE_MS, now) || (s.quarantined ?? 0) > 0) return 'warning';
    return 'healthy';
  }
  const o = b.onServer;
  if (!o || o.status === null || o.status === 'failed') return 'down';
  const pushes = b.offsite ?? [];
  if (pushes.length === 0 || pushes.some((p) => p.status !== 'ok')) return 'down';
  const r = b.restoreTest;
  if (!r || r.status === 'failed') return 'down';
  // The AWS copy counts once the collector reports it (undefined = an older collector).
  const a = b.awsCopy;
  const ra = b.restoreTestAws;
  if (a === null || a?.status === 'failed' || a?.status === null || ra?.status === 'failed') return 'down';
  if (o.status === 'degraded' || older(o.lastSetFinishedAt, SET_STALE_MS, now) || older(r.finishedAt, RESTORE_STALE_MS, now)) return 'warning';
  if (a && (older(a.checkedAt, SET_STALE_MS, now) || (a.failedLastRun ?? 0) > 0)) return 'warning';
  if (a !== undefined && (!ra || older(ra.finishedAt, AWS_RESTORE_STALE_MS, now))) return 'warning';
  return 'healthy';
}
